import type { VoiceCallPhase } from '../types/voice-call'

/**
 * Pure decision helpers for the voice-call connection lifecycle. Kept free of
 * SDK / store imports so the exact rules that decide whether a call survives a
 * Chime stop, a heartbeat gap, or an end-of-call sweep are unit-testable.
 */

/**
 * Chime `MeetingSessionStatusCode` values this module keys on. The SDK enum is
 * a stable public API; the controller's RETRYABLE_INITIAL_CONNECT_CODES already
 * references the same numeric values (see use-voice-call-controller.ts).
 */
export const CHIME_STATUS_AUDIO_JOINED_FROM_ANOTHER_DEVICE = 2
export const CHIME_STATUS_MEETING_ENDED = 5

export type MeetingStopAction =
  /** Session died before media ever started — controller decides on a rejoin. */
  | 'connect-failed'
  /**
   * Chime dropped THIS attendee because the same user joined from another
   * device (multi-device Switch). Must be a SILENT local handoff to
   * `elsewhere` — never an END/INTERRUPTED broadcast, which would race the
   * DEVICE_CLAIM signal and end the call for every participant.
   */
  | 'kicked-other-device'
  /** The meeting itself ended server-side — end locally, nothing to notify. */
  | 'meeting-ended'
  /** Mid-call drop of an established session — grace-terminate applies. */
  | 'disconnected'
  /** Stop for a session we already consider stopped — nothing to do. */
  | 'ignore'

/**
 * Classify an `audioVideoDidStop` for an adapter session.
 *
 * @param code Chime MeetingSessionStatusCode (null when the SDK gave none)
 * @param mediaStarted audioVideoDidStart fired at least once this session
 * @param connected the adapter still considers itself connected
 */
export function classifyMeetingStop(input: {
  code: number | null
  mediaStarted: boolean
  connected: boolean
  /** True while this adapter instance is tearing itself down. */
  disposing?: boolean
}): MeetingStopAction {
  // Hangup stop() can fire after the adapter has already begun dispose (and
  // on some WebView/SDK paths, after mediaStarted is cleared). That is
  // teardown, not an initial-connect failure — the 2026-08-19 post-hangup
  // `action:connect-failed` red herring.
  if (input.disposing) return 'ignore'
  if (input.code === CHIME_STATUS_AUDIO_JOINED_FROM_ANOTHER_DEVICE) {
    return 'kicked-other-device'
  }
  if (!input.mediaStarted) return 'connect-failed'
  if (input.code === CHIME_STATUS_MEETING_ENDED) return 'meeting-ended'
  if (!input.connected) return 'ignore'
  return 'disconnected'
}

export type HeartbeatMissDecision =
  /** Peer heartbeat is fresh — nothing to do. */
  | 'keep'
  /**
   * Heartbeats are stale but Chime still has live media AND a present remote
   * attendee — the SIGNALING channel is degraded (AppSync blip, backgrounded
   * publisher), not the call. Extend the deadline instead of ending a working
   * call with a false INTERRUPTED.
   */
  | 'defer'
  /** Heartbeats stale and media agrees the peer is gone — end the call. */
  | 'end'

export function decideOnHeartbeatMiss(input: {
  lastPeerHeartbeatAt: number | null | undefined
  connectedAt: number | null | undefined
  now: number
  missMs: number
  mediaConnected: boolean
  remotePresent: boolean
}): HeartbeatMissDecision {
  const last = input.lastPeerHeartbeatAt ?? input.connectedAt ?? input.now
  if (input.now - last <= input.missMs) return 'keep'
  if (input.mediaConnected && input.remotePresent) return 'defer'
  return 'end'
}

/**
 * `senderDeviceId` stamped by the `voice-call-native-ack` lambda when it relays
 * a native (CallKit) answer as an ACCEPT signal. It identifies the RELAY, not a
 * device: the ACCEPT fans out to every device of the callee — including the one
 * that answered — with `senderId` === our own user id, so it is otherwise
 * indistinguishable from a sibling-device answer.
 */
export const NATIVE_ACK_SENDER_DEVICE_ID = 'native-ack'

/**
 * Whether a same-user ACCEPT is this device's OWN answer echoing back through
 * the native-ack relay, and must therefore NOT be treated as "another device
 * answered".
 *
 * The locked-answer failure: a CallKit answer relays ACCEPT via native-ack; it
 * comes back to the answering device, which (still `incoming`, because the
 * suspended WebView had not run `acceptCall` yet) treated it as a sibling
 * answer and disposed the native media session that had just joined the
 * meeting — leaving the caller on "Connecting…" forever.
 *
 * Deliberately narrow so the multi-device Switch behaviour is untouched:
 *  - ACCEPT only (REJECT/END/BUSY from a sibling still tear this ring down),
 *  - only for a call THIS device answered locally or already owns native media
 *    for (`answeredHereCallId` / `nativeMediaCallId` are device-local markers),
 *  - only for the native-ack relay stamp — a REAL sibling device's ACCEPT
 *    carries that device's own id and keeps its existing behaviour, so a genuine
 *    answer-elsewhere still hands off (DEVICE_CLAIM / AudioJoinedFromAnotherDevice
 *    remain the canonical Switch mechanisms).
 */
export function shouldIgnoreOwnAnswerEcho(input: {
  signalType: string
  signalCallId: string
  senderDeviceId?: string | null
  answeredHereCallId: string | null
  nativeMediaCallId: string | null
  session: { callId: string; phase: VoiceCallPhase } | null | undefined
}): boolean {
  if (input.signalType !== 'ACCEPT') return false
  const relayed =
    !input.senderDeviceId || input.senderDeviceId === NATIVE_ACK_SENDER_DEVICE_ID
  if (!relayed) return false
  const answeredHere =
    input.answeredHereCallId === input.signalCallId ||
    input.nativeMediaCallId === input.signalCallId
  if (!answeredHere) return false
  const session = input.session
  if (!session || session.callId !== input.signalCallId) return false
  return session.phase !== 'ended' && session.phase !== 'idle'
}

/**
 * Whether this iOS VOICE join should start (or keep) the native Chime SDK
 * media engine rather than WKWebView WebRTC.
 *
 * Incoming CallKit answers always needed this (WebKit mutes background
 * capture). Outgoing / foreground iOS voice previously stayed on the JS
 * adapter "until device-validated" — telemetry from 2026-08-19 (Andre iOS
 * outgoing ↔ Zach Android) showed that path is the one-way-audio failure:
 * WebView getUserMedia on Bluetooth without CallKit ownership, AVAudioSession
 * interruptions with no resume, HTML audio downlink paused. Native
 * `audioVideo.start()` (no CallKit) already exists for this case.
 *
 * VIDEO stays on the JS adapter (no native camera yet).
 */
export function shouldInitiateNativeIosVoiceJoin(input: {
  isVideo: boolean
  nativeAudioEnabled: boolean
}): boolean {
  return !input.isVideo && input.nativeAudioEnabled
}

export type ConnectingStallAction = 'keep' | 'promote' | 'retry' | 'end' | 'wait-visible'

/**
 * Connecting-stall watchdog decision. The 2026-08-18 Zach (web) → Jaiden (iOS)
 * VIDEO call was INTERRUPTED at +30.7s: Jaiden answered from a backgrounded
 * WebView, `chime:join:start` ran, `chime:mic:started` NEVER fired (WebKit
 * background capture mute), and the hard cap killed a call whose peer was
 * already talking. While the WebView is hidden we MUST wait for foreground
 * rather than END; if media is actually up, PROMOTE instead of stranding
 * "Connecting…".
 */
export function decideConnectingStall(input: {
  connectingForMs: number
  hardCapMs: number
  stallForMs: number
  stallRetryAfterMs: number
  mediaConnected: boolean
  joinInFlight: boolean
  alreadyRetried: boolean
  documentHidden: boolean
  /**
   * When true (1:1 CALLER), local media is not two-way. Do not promote to
   * active / COMPLETED while the callee never joined Chime (87e4bcd2).
   */
  requireRemote?: boolean
  remotePresent?: boolean
}): ConnectingStallAction {
  const waitingForRemote = input.requireRemote === true && input.remotePresent !== true
  if (input.mediaConnected && !waitingForRemote) return 'promote'
  if (input.documentHidden) return 'wait-visible'
  if (input.connectingForMs > input.hardCapMs) return 'end'
  if (input.joinInFlight || input.mediaConnected) return 'keep'
  if (input.stallForMs < input.stallRetryAfterMs) return 'keep'
  return input.alreadyRetried ? 'end' : 'retry'
}

/**
 * Whether the deferred (4s) post-end sweep may null the session store. Guards
 * the sweep against a NEW call started right after the previous one ended —
 * the unguarded timer wiped the fresh session mid-dial (fast redial bug).
 */
export function shouldClearEndedSession(
  current: { callId: string; phase: VoiceCallPhase } | null | undefined,
  endedCallId: string,
): boolean {
  if (!current) return false
  if (current.callId !== endedCallId) return false
  return current.phase === 'ended' || current.phase === 'idle'
}
